import { useState, useEffect } from "react";
import { useViewport } from "@/hooks/useViewport";
import Modal, { ModalSize } from "./Modal";
import Selector from "../Selector";
import PrimaryButton from "../PrimaryButton";
import LANGUAGE_OPTIONS, { MultilingualOption } from "@/constants/multilingual";
import { useForm } from "@/features/tutor/context/FormContext";
import { recordPreferredVoice } from "@/features/tutor/context/preferredVoice";
import { TutorMode } from "@/constants/mode";
import { useModalStore, ModalType } from "@/store/useModalStore";
import { useAuthStore } from "@/store/useAuthStore";
import { renderRadioButton } from "../selectors/RadioButtonRenderer";
import { resolveVoiceForLanguage } from "@/constants/audioVoice";
import "./lesson-entry.css";

const ChangeLanguageModal = ({
  isOpen,
  onClose,
  returnModalType = ModalType.SESSION_START,
  returnModalProps,
}: {
  isOpen: boolean;
  onClose: () => void;
  returnModalType?: ModalType;
  returnModalProps?: Record<string, unknown>;
}) => {
  const [inputValue, setInputValue] = useState("");
  const { formData, updateField, mode } = useForm();
  const { openModal, closeModal } = useModalStore();
  const { isMobile } = useViewport();
  const { user } = useAuthStore();
  const showBetaVoices = Boolean(user?.config?.enableBetaTtsProviders);

  // Staged like ChangeVoiceModal: browsing languages must not commit one.
  const [draftLanguage, setDraftLanguage] = useState<MultilingualOption>(
    formData.language,
  );
  useEffect(() => {
    if (isOpen) setDraftLanguage(formData.language);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredOptions = LANGUAGE_OPTIONS.filter((option) => {
    const query = inputValue.toLowerCase();
    return (
      option.label.toLowerCase().includes(query) ||
      (option.sublabel1 ?? "").toLowerCase().includes(query)
    );
  });

  // The whole bundle goes back untouched. Rebuilding it field by field is what
  // used to drop the caller's assessment props on the way home.
  const getReturnModal = () => ({
    type: returnModalType,
    props: returnModalProps || {},
  });

  const handleLanguageChange = (option: MultilingualOption) => {
    setDraftLanguage(option);
  };

  const handleLanguageSelect = () => {
    updateField("language", draftLanguage);

    // A confirmed language that lacks the current voice swaps it, and the
    // swap is what gets recorded. includeBeta keeps the offered set the same as
    // the one ChangeVoiceModal picked from.
    const speed = formData.voice?.speed ?? "medium";
    const nextVoiceId = resolveVoiceForLanguage(
      draftLanguage.value,
      formData.voice?.voiceId,
      { includeBeta: showBetaVoices },
    );
    if (nextVoiceId !== formData.voice?.voiceId) {
      updateField("voice", { voiceId: nextVoiceId, speed });
    }

    if (mode !== TutorMode.PREVIEW) {
      recordPreferredVoice(nextVoiceId, draftLanguage.value, speed);
    }

    const nextModal = getReturnModal();
    closeModal();
    openModal(nextModal.type, nextModal.props);
  };

  const mixedLanguages = filteredOptions.filter(
    (option) => option.multilingual,
  ) as MultilingualOption[];
  const singleLanguages = filteredOptions.filter(
    (option) => !option.multilingual,
  ) as MultilingualOption[];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      showCloseButton={false}
      size={ModalSize.sm}
      className="et-lesson-entry-modal et-entry-picker p-0"
    >
      <div className="flex flex-col justify-start items-center space-y-4 p-6 w-full relative flex-1 overflow-hidden">
        <div className="flex flex-row items-center gap-3 w-full">
          <button
            type="button"
            onClick={() => {
              const nextModal = getReturnModal();
              openModal(nextModal.type, nextModal.props);
            }}
            className="flex-none w-[40px] h-[40px] rounded-[6px] bg-[#edf0e5] text-[#28513e] border-none cursor-pointer flex items-center justify-center transition-colors duration-150 hover:bg-[#dfe7d7]"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-[18px] h-[18px]"
            >
              <path d="M10 18 L4 12 L10 6" />
              <path d="M4 12 H20" />
            </svg>
          </button>
          <div className="flex-1 flex items-center gap-2.5 bg-[#fdfbf6] border border-[#d9d7ca] rounded-[6px] px-3.5 py-2.5 transition-colors duration-150 focus-within:border-[#28513e]">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              className="w-[18px] h-[18px] text-[#28543D] flex-none"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20 L16 16" />
            </svg>
            <input
              type="text"
              placeholder="Search for a language"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="flex-1 border-none outline-none bg-transparent font-inherit text-[14.5px] text-[#28513e] placeholder:text-[#7b857c]"
            />
          </div>
        </div>
        {mixedLanguages.length === 0 && singleLanguages.length === 0 ? (
          <p className="text-center text-lg p-4 text-[#637264] flex justify-center items-start flex-1">
            No languages found
          </p>
        ) : (
          <>
            <div
              id="languages-container"
              className="flex flex-col items-start space-y-4 py-4 pb-32 w-full overflow-y-auto flex-1 min-h-0 max-h-full"
            >
              {mixedLanguages.length > 0 && (
                <>
                  <div className="flex items-baseline gap-1.5 w-full">
                    <span className="text-[17px] font-bold text-[#28513e]">
                      Mixed
                    </span>
                    <span className="font-libre-baskerville text-[17px] italic text-[#28513e]">
                      Languages
                    </span>
                  </div>
                  <Selector
                    options={mixedLanguages}
                    selectedValue={draftLanguage.value}
                    onChange={handleLanguageChange}
                    label="languages"
                    truncateAt={mixedLanguages.length}
                    renderOption={renderRadioButton}
                    numColumns={1}
                  />
                </>
              )}
              {singleLanguages.length > 0 && (
                <>
                  <div className="flex items-baseline gap-1.5 w-full">
                    <span className="text-[17px] font-bold text-[#28513e]">
                      Single
                    </span>
                    <span className="font-libre-baskerville text-[17px] italic text-[#28513e]">
                      Languages
                    </span>
                  </div>
                  <Selector
                    options={singleLanguages}
                    selectedValue={draftLanguage.value}
                    onChange={handleLanguageChange}
                    label="languages"
                    truncateAt={singleLanguages.length}
                    renderOption={renderRadioButton}
                    numColumns={isMobile ? 1 : 2}
                  />
                </>
              )}
            </div>
            <div className="absolute bottom-3 left-0 right-0 pt-6 pb-2 flex flex-col items-center space-y-2 px-8 bg-gradient-to-t from-[#fbf8f1] to-[#fbf8f1]/30 rounded-b-[12px] modal-landscape:bottom-1">
              <PrimaryButton
                onClick={handleLanguageSelect}
                children="Choose Language"
                disabled={!draftLanguage}
                className="modal-portrait:!p-3 modal-portrait:!text-base modal-landscape:!p-3 modal-landscape:!text-base"
              />
              {draftLanguage?.multilingual && (
                <p className="text-center text-[#52685a] font-semibold italic text-sm pt-3 pb-1">
                  For mixed languages, in noisy environments, speech recognition
                  may be less accurate.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ChangeLanguageModal;
